/**
 * App-facing types for the Star printer service.
 *
 * Everything the rest of the app needs from `@am3nd-org/capacitor-starprint-plugin`
 * is re-exported here so that the plugin package is imported in exactly one place
 * (`star-print.service.ts`). UI/store code imports from `src/services/printers`.
 */
import type {
  StarPrinter,
  StarPrintError,
  StarPrintResult,
  StarPrintTextLine,
  StarPrintTextSize,
  StarPrinterStatus,
  StarPrinterTarget,
  StarPrinterInterface,
  StarPrintReceiptItem,
  StarPrinterConnection,
  DiscoverPrintersResult,
  StarPrintReceiptRequest,
  NativeAvailabilityResult,
  BluetoothAuthorizationResult,
  BluetoothAuthorizationStatus,
} from '@am3nd-org/capacitor-starprint-plugin';

export type {
  StarPrinter,
  StarPrintError,
  StarPrintResult,
  StarPrintTextLine,
  StarPrintTextSize,
  StarPrinterStatus,
  StarPrinterTarget,
  StarPrinterInterface,
  StarPrintReceiptItem,
  StarPrinterConnection,
  DiscoverPrintersResult,
  StarPrintReceiptRequest,
  NativeAvailabilityResult,
  BluetoothAuthorizationResult,
  BluetoothAuthorizationStatus,
};

/** Whether a printer connects over Bluetooth (vs. LAN/Wi-Fi or USB). */
export function isBluetoothInterface(interfaceType: StarPrinterInterface): boolean {
  return interfaceType === 'bluetooth' || interfaceType === 'ble';
}

/**
 * The default printer persisted in `system_config` (key `defaultStarPrinter`).
 *
 * We store the full `connection` object so the service can reconnect headlessly
 * without a fresh discovery — LAN needs ip/port, Bluetooth the MAC, USB the port
 * name. `schemaVersion` lets a future read discard an incompatible blob.
 */
export interface SavedPrinter {
  identifier: string;
  model: string;
  name?: string;
  interfaceType: StarPrinterInterface;
  connection: StarPrinterConnection;
  savedAt: string; // ISO 8601
  schemaVersion: 1;
}

/**
 * Default discovery scope. USB is intentionally excluded — the legacy Cordova
 * plugin's `portDiscovery('All')` USB scan crashed with an uncaught
 * `SecurityException`, so USB is an explicit opt-in in the settings UI.
 */
export const DEFAULT_DISCOVERY_INTERFACES: StarPrinterInterface[] = ['lan', 'bluetooth'];

/**
 * A discovery interface that was skipped or failed, with a structured reason —
 * surfaced from the native layer's `configurationIssues` (e.g. Bluetooth permission
 * denied, Local Network not allowed, or an interface unsupported in this build). The
 * plugin's own `DiscoverPrintersResult` type does not yet declare this field, but the
 * native layer returns it, so the service reads it and re-exposes it here.
 */
export interface StarPrintConfigurationIssue {
  code: string;
  message: string;
  recoverable: boolean;
  interfaceType?: StarPrinterInterface;
}

/** Result of a discovery run: printers found plus any per-interface issues. */
export interface PrinterDiscoveryResult {
  printers: StarPrinter[];
  configurationIssues: StarPrintConfigurationIssue[];
  message?: string;
}
